/**
 * @description Please be careful. @ts-nocheck hides all TS warnings.
 * @todo: add types to the file
 */
// @ts-nocheck
import React, { useContext } from 'react';
import { PasswordStrengthMeterContext } from '@components-member/PasswordStrengthMeter/PasswordStrengthMeter';
import { KeyMapKeys } from '@locales/lokalise/keyMap';
import InputsTable from 'components/shared/configConstructor/inputsTable';
import { CONSTANTS } from 'components/shared/constants';
import i18next from 'i18next';
import 'styles/shared/formTableStyle.scss';
import { DateTimeRange } from './DateTimeRange';
import { DynamicDropdown } from './DynamicDropdown';
import { BaseExtendedSelector } from './ExtendedSelectors';
import { FormGeneratorActions } from './FormGeneratorActions';
import { FormGeneratorErrorGuard } from './FormGeneratorErrorGuard';
import { IInputProps, Input } from './Input';
import { InputWithSlider } from './InputWithSlider';
import { MultiSelect } from './MultiSelect';
import NumberInput from './NumberInput';
import { PhoneInput } from './PhoneInput';
import { RadioGroup } from './RadioGroup';
import { Select } from './Select';
import { Switcher } from './Switcher';
import { UploadFileInput, UploadMultiFileInput } from './UploadFileInput';
import Checkbox from './checkbox';
import CustomSelect from './customSelect';
import DatetimePicker from './datetimePicker';
import DropdownDatePicker from './dropdownDatePicker';
import Editor from './editor';
import { IFormField, IFieldSet, IModalFormField, IFormAdditionalField, FieldValue } from './formField.interface';
import { SelectWithExtendedOptions } from './selectWithExtendedOptions';
import Separator from './separator';
import TextArea from './textArea';
import {
  isHidden,
  FormGeneratorValidationProps,
  withFormGeneratorValidation,
  IValidationData,
} from './withFormGeneratorValidation';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function FormGenerator(_fields: any, _triggers = {}) {
  let fields = _fields;
  const getFieldsFromOuterScope = () => fields;
  let triggers = _triggers;

  class Form extends React.Component<FormGeneratorValidationProps> {
    state = {};

    inputsTables = {};

    oldValues = {};

    /** @description used through the ref externally. Please do not delete. */
    validate = (...props: any) => this.props.validate(...props);

    update(_fields: any, _triggers: any) {
      fields = _fields;
      triggers = _triggers ?? triggers;

      this.resetValidation();
    }

    trigger(name: string, value: any, formFields?: any) {
      if (triggers[name]) {
        triggers[name](this, value, formFields);
      }
    }

    onChange = (name, value, section, ...parameters) => {
      const { onChange } = this.props;

      this.updateOldValues(name, section);

      onChange.apply(this, [name, value, section, ...parameters]);
      this.updateFiledValidation(name, section);
      if (triggers[name]) {
        triggers[name].apply(this, [this, value, ...parameters]);
      }
    };

    updateOldValues = (name, section) => {
      const { form } = this.props;

      if (section) {
        this.oldValues[section] = this.oldValues[section] ?? {};
        this.oldValues[section][name] = form[section][name];
      } else {
        this.oldValues[name] = form[name];
      }
    };

    onDatetimeChange = ({ name, value }) => {
      const { onChange } = this.props;

      onChange(name, value);
      this.updateFiledValidation(name);

      this.setState({ form: this.state?.form });
    };

    updateFiledValidation = (name: string, section?: string) => {
      const { form } = this.props;
      const validation = this.props.validationState;
      const isFieldValid = section ? validation[section][name].valid : validation[name].valid;

      if (!isFieldValid) {
        const currentSection = section
          ? fields.find((field) => field.type === 'section' && field.name === section)
          : null;

        const changedField = (currentSection ? currentSection.fields : fields).find((field) => field.name === name);

        const fieldValidationResult = this.props.checkField(section ? form[section] : form, changedField);
        const updatedValidation = section
          ? { ...validation, [section]: { ...validation[section], [name]: fieldValidationResult } }
          : { ...validation, [name]: fieldValidationResult };

        this.props.setValidationState(updatedValidation);
      }
    };

    resetValidation = () => {
      this.props.setInitialValidationState(fields);
    };

    generateFields = (fields: IFormField[], form: any, section?: string) => {
      const { params, disabled } = this.props;

      return fields.map((field) => {
        if (isHidden(this, field)) {
          return null;
        }

        const valuePathSegs = Array.isArray(field.name) ? field.name : field.name && field.name.split('.');
        const value = valuePathSegs ? valuePathSegs.reduce((prev, curr) => prev && prev[curr], form) : form[field.name];

        if (disabled) {
          field = { ...field, attributes: { ...field.attributes, disabled: true } };
        }

        const className = field.className ? `${field.className || ''} custom-width ` : '';

        field = { ...field, className };

        if (field.component) {
          const Field = field.component;

          return (
            <Field
              key={field.name}
              {...field}
              value={value}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'datetime' && field.view === 'dropdown') {
          return (
            <DropdownDatePicker
              key={field.name}
              {...field}
              value={value}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              placeholders={this.getValidationField(field.name, section).placeholders}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'datetime') {
          return (
            <DatetimePicker
              key={field.name}
              {...field}
              value={value}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'dateTimeRange') {
          return (
            <DateTimeRange
              key={field.name}
              {...field}
              onChange={(value) => {
                this.onDatetimeChange(value);
              }}
              defaultValue={field.defaultValue}
              options={field.options}
              valid={this.getValidationField(field.name).valid}
              validationMessage={this.getValidationField(field.name).message}
              className={field.className}
            />
          );
        }

        if (field.type === 'select') {
          const options = this.getSelectOptions(field.options, form);
          const attributes = this.getFieldAttributes(field, form);

          return (
            <Select
              key={field.name}
              {...field}
              value={value}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              params={params}
              attributes={attributes}
              options={options}
            />
          );
        }

        if (field.type === 'customSelect') {
          const options = this.getSelectOptions(field.options, form);
          const attributes = this.getFieldAttributes(field, form);

          return (
            <CustomSelect
              key={field.name}
              {...field}
              value={value}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              params={params}
              attributes={attributes}
              options={options}
              showChooseText={true}
            />
          );
        }

        if (isNumericType(field.type)) {
          return (
            <NumberInput
              key={field.name}
              {...field}
              value={value}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              placeholders={this.getValidationField(field.name, section).placeholders}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'checkbox') {
          return (
            <Checkbox
              key={field.name}
              {...field}
              value={value}
              onChange={({ target }) => this.onChange(target.name, target.checked, section)}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'switcher') {
          return (
            <Switcher
              key={field.name}
              {...field}
              value={value}
              onChange={(state) => this.onChange(field.name, state, section)}
              valid={this.getValidationField(field.name, section).valid === false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'textarea') {
          return (
            <TextArea
              key={field.name}
              {...field}
              value={value ? value : ''}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'array') {
          const getLabel = (attributes, name) => {
            const { language = i18next.language } = this.props;

            const lang = language && language.split('-')[0];

            if (attributes && attributes.localization) {
              return attributes.localization[lang] || attributes.localization.default || name;
            }

            return i18next.t(name as KeyMapKeys);
          };

          const valid = this.getValidationField(field.name, section).valid !== false;
          const validationMessage = this.getValidationField(field.name, section).message;

          return (
            <div key={field.name} className="form__fieldset">
              <div className="form__field textarea__field">
                <span className="form__label">
                  {i18next.t(field.title)}
                  {field.required && <span className="form__required">*</span>}
                </span>

                {!valid && <span className="form__error">{i18next.t(validationMessage)}</span>}
                <InputsTable
                  classNamePreffix="form-table"
                  key={field.name}
                  name={field.name}
                  value={value || []}
                  onChange={(e) => this.onChange(e.target.name, e.target.value, section)}
                  members={field}
                  getLabel={getLabel}
                  attributes={field.attributes}
                  disabled={false}
                  ref={(field) => {
                    this.props.setInputsTableProp(field, field?.props?.name);
                  }}
                />
              </div>
            </div>
          );
        }

        if (field.type === 'multiselect') {
          return (
            <MultiSelect
              key={field.name}
              {...field}
              value={value}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'groupSelector') {
          const isValid = this.getValidationField(field.name, section).valid !== false;
          const validationMessage = this.getValidationField(field.name, section).message;

          return (
            <BaseExtendedSelector
              key={field.name}
              required={field.required}
              valid={isValid}
              validationMessage={validationMessage}
              className={field.className}
              createButtonProps={field.createButtonProps}
              name={field.name}
              value={value}
              subtype={field.subtype}
              title={field.title}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              groupTitle={field.groupTitle}
              groupItemList={field.groupItemList}
              groupAttributes={field.groupAttributes}
              selectedGroupItemList={field.selectedGroupItemList}
              groupByDefault={field.groupByDefault}
              chooseText={true}
              renderAfter={field.renderAfter}
              selectedGroupOptionsAttributes={field.selectedGroupOptionsAttributes}
            />
          );
        }

        if (field.type === 'file') {
          return (
            <UploadFileInput
              {...field}
              key={field.name}
              value={value}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'multiFile') {
          return (
            <UploadMultiFileInput
              {...field}
              key={field.name}
              value={value}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'section') {
          return (
            <div key={field.name} className="form__section">
              {field.sectionTitle && <div className="form__section-title">{i18next.t(field.sectionTitle)}</div>}
              {this.generateFields(field.fields, value, field.name)}
            </div>
          );
        }

        if (field.type === 'phone') {
          return (
            <PhoneInput
              key={field.name}
              {...field}
              value={value}
              onChange={(name, value, data) => this.onChange(name, value, section, data)}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'editor') {
          return (
            <Editor
              key={field.name}
              {...field}
              value={value}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }

        if (field.type === 'separator') {
          return <Separator key={field.name || field.title} {...field} />;
        }
        if (field.type === 'radiogroup') {
          return (
            <RadioGroup
              key={field.name}
              {...field}
              value={form[field.name]}
              onChange={({ target }) => {
                this.onChange(target.name, target.value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }
        if (field.type === 'dynamicDropDown') {
          return (
            <DynamicDropdown
              key={field.name}
              {...field}
              value={value}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }
        if (field.type === 'inputWithSlider') {
          return (
            <InputWithSlider
              key={field.name}
              {...field}
              value={value ?? ''}
              onChange={(name, value) => this.onChange(name, value, section)}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              form={form}
              params={params}
            />
          );
        }
        if (field.type === 'selectWithExtendedOptions') {
          return (
            <SelectWithExtendedOptions
              key={field.name}
              name={field.name}
              title={field.title}
              options={field.options}
              descriptionComponent={field.descriptionComponent}
              notificationComponent={field.notificationComponent}
              onChange={(name, value) => {
                this.onChange(name, value, section);
              }}
              required={field.required}
              valid={this.getValidationField(field.name, section).valid !== false}
              validationMessage={this.getValidationField(field.name, section).message}
              className={field.className}
              placeholder={field.placeholder}
              getRequestData={this.props.getRequestData}
              setOption={this.props.setOption}
              selectedOptionValue={field.defaultValue}
              attributes={field.attributes}
              notificationsWarning={this.props.notificationsWarning}
            />
          );
        }

        return (
          <InputWithContext
            key={field.name}
            {...field}
            value={value ?? ''}
            onChange={(name, value) => this.onChange(name, value, section)}
            valid={this.getValidationField(field.name, section).valid !== false}
            validationMessage={this.getValidationField(field.name, section).message}
            placeholders={this.getValidationField(field.name, section).placeholders}
            form={form}
            params={params}
          />
        );
      });
    };

    getSelectOptions = (options, form) => (typeof options === 'function' ? options(form) : options);

    getFieldAttributes = (field, form) =>
      field.getAttributes ? { ...field.attributes, ...field.getAttributes(form) } : field.attributes;

    getValidationField(name: string, section?: string): IValidationData {
      const validation = this.props.validationState;

      return section ? validation[section][name] : validation[name];
    }

    returnActionWithValidation = (action, validationPath) => () => {
      if (this.validate(validationPath)) {
        return action();
      } else {
        return null;
      }
    };

    returnActionWithNoValidation = (action) => () => {
      this.resetValidation();
      return action();
    };

    onSubmitForm = (event) => {
      event.preventDefault();
      const { actions } = this.props;
      const action = Array.isArray(actions) && actions.find((action) => action.type === 'submit');

      if (!action) {
        return null;
      }

      return action.noValidate
        ? this.returnActionWithNoValidation(action.onClick)
        : this.returnActionWithValidation(action.onClick, action.validationPath);
    };

    render() {
      const { title, actions, form, disabled, before, className, beforeActions, after } = this.props;
      const additionalClass = className || '';

      return (
        <FormGeneratorErrorGuard>
          <form className={`form ${additionalClass}`} autoComplete="off" noValidate onSubmit={this.onSubmitForm}>
            {before}
            {title && <p className="form__title">{i18next.t(title as KeyMapKeys)}</p>}

            <div className="form__field-list">{this.generateFields(fields, form)}</div>

            {beforeActions}

            <FormGeneratorActions
              returnActionWithValidation={this.returnActionWithValidation}
              returnActionWithNoValidation={this.returnActionWithNoValidation}
              actions={actions}
              disabled={disabled}
            />

            {after}
          </form>
        </FormGeneratorErrorGuard>
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return withFormGeneratorValidation(Form, getFieldsFromOuterScope) as React.ExoticComponent<any> & {
    update: (fields: IFormField[] | IFieldSet[] | IFormAdditionalField[]) => void;
    trigger: (name: string, value: any, formFields?: any) => void;
    validate: () => boolean;
    props: any;
  };
}

const isNumericType = (type: string) => {
  return ['number', 'int32'].indexOf(type) > -1;
};

const stateInitializer =
  (ctx?: unknown) =>
  (
    state: Record<string, unknown[] | boolean | string | { from: number; to: number } | null | FieldValue>,
    field: IFormField,
  ) => {
    if (field.type === 'multiselect' || field.type === 'array') {
      state[field.name] = [];
    } else if (field.type === 'section') {
      state[field.name] = getInitialState(field.fields);
    } else if (field.type === 'checkbox' || field.type === 'switcher') {
      state[field.name] = false;
    } else if (field.type === 'dateTimeRange' || field.type === 'numberRange') {
      state[field.name] = CONSTANTS.DEFAULT_RANGE_VALUE;
    } else if (field.type === 'numberRangeWithSlider') {
      state[field.name] = { from: 0, to: 0 };
    } else if (field.type === 'file') {
      state[field.name] = null;
    } else if (field.type === 'multiFile') {
      state[field.name] = null;
    } else if (field.type === 'separator') {
      return state;
    } else if (field.type === 'hidden') {
      state[field.name] = null;
    } else if (field.type === 'groupSelector') {
      state[field.name] = '';
    } else if (field.type === 'datetime') {
      state[field.name] = '';
    } else if (field.type === 'select') {
      state[field.name] = '';
    } else {
      state[field.name] = '';
    }

    if (typeof field.defaultValue !== 'undefined') {
      const { defaultValue } = field;

      state[field.name] = typeof defaultValue === 'function' ? defaultValue(ctx) : defaultValue;
    }

    return state;
  };

export const getInitialState = (
  fields: IFormField[] | IModalFormField[] | IFieldSet[] | IFormAdditionalField[],
  ctx?: any, // Parent Component Props
) => {
  return fields.reduce(stateInitializer(ctx), {});
};

export const mergeState = (fields: IFormField[], oldState: any) => {
  const initialState = getInitialState(fields);

  return fields.reduce((state, field) => {
    if (field.type === 'section') {
      state[field.name] = mergeState(field.fields, oldState[field.name]);
    } else if (oldState[field.name] != null) {
      state[field.name] = oldState[field.name];
    } else {
      state = stateInitializer()(state, field);
    }
    return state;
  }, initialState);
};

const InputWithContext = (props: IInputProps) => {
  const passwordStrengthMeter = useContext(PasswordStrengthMeterContext);

  return (
    <Input
      {...props}
      renderAfter={props.attributes?.usePasswordStrengthMeterContext ? passwordStrengthMeter : props.renderAfter}
    />
  );
};

export default FormGenerator;
